/**
 * PlateCodesManager — manage codes for a specific (emirate, category) pair.
 * Route: /control/cloudgm-business/plate-codes/:emirate/:category
 *
 * Backend:
 *   GET    /api/admin/plate-codes/codes?emirate=...&category=...
 *   POST   /api/admin/plate-codes/codes
 *   PUT    /api/admin/plate-codes/codes/{id}
 *   DELETE /api/admin/plate-codes/codes/{id}
 *
 * Image storage: base64 data-URLs (or external https URLs).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2, Plus, Trash2, Edit3, Image as ImageIcon, X, Save, Upload, Languages,
  CheckCircle2, AlertTriangle, ImageOff, Hash, Car, Bike, Crown,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import DashboardLayout from '../components/DashboardLayout';
import UAEPlate, { hasPlateDesign } from '../components/plates/UAEPlate';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
// (TranslateButton no longer needed after removing AR/EN description fields)

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API_URL}/api/admin/plate-codes`;

const EMIRATES_META = {
  abu_dhabi:       { ar: 'أبوظبي',     en: 'Abu Dhabi',       color: '#10b981' },
  dubai:           { ar: 'دبي',        en: 'Dubai',           color: '#3b82f6' },
  sharjah:         { ar: 'الشارقة',    en: 'Sharjah',         color: '#f59e0b' },
  ajman:           { ar: 'عجمان',      en: 'Ajman',           color: '#8b5cf6' },
  umm_al_quwain:   { ar: 'أم القيوين', en: 'Umm Al Quwain',   color: '#ec4899' },
  ras_al_khaimah:  { ar: 'رأس الخيمة', en: 'Ras Al Khaimah',  color: '#06b6d4' },
  fujairah:        { ar: 'الفجيرة',    en: 'Fujairah',        color: '#ef4444' },
};

const CATEGORY_META = {
  private:    { ar: 'خصوصي',  en: 'Private',    color: '#3b82f6', icon: Car },
  motorcycle: { ar: 'دراجة',  en: 'Motorcycle', color: '#f59e0b', icon: Bike },
  classic:    { ar: 'كلاسيك', en: 'Classic',    color: '#8b5cf6', icon: Crown },
};

const MAX_IMAGE_BYTES = 500 * 1024; // 500KB

const EMPTY_FORM = {
  code: '',
  code_ar: '',
  show_on_plate: true,
  label_ar: '',
  label_en: '',
  image_url: '',
  is_active: true,
  sort_order: 0,
};

export default function PlateCodesManager() {
  const navigate = useNavigate();
  const { emirate, category } = useParams();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const emirateMeta = EMIRATES_META[emirate];
  const categoryMeta = CATEGORY_META[category];
  // هل يوجد تصميم لوحة حقيقي لهذه الفئة؟ (12 تصميماً من المصمم)
  // ملاحظة: لوحة التحكم تعرض الكود فقط — الأرقام يكتبها العملاء/التجار في منصاتهم
  const designAvailable = hasPlateDesign(emirate, category);

  // Guard: invalid params
  useEffect(() => {
    if (!emirateMeta || !categoryMeta) {
      toast.error(isAr ? 'إمارة أو فئة غير صحيحة' : 'Invalid emirate/category');
      navigate('/control/cloudgm-business/plate-codes');
    }
  }, [emirateMeta, categoryMeta, navigate, isAr]);

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // null | 'new' | code object
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [translatingField, setTranslatingField] = useState(null); // 'code_ar' | 'code_en'
  const fileInputRef = useRef(null);

  // Translate via the LLM-backed /api/utils/translate endpoint — free-form fields,
  // the user can always override the result before saving.
  const autoTranslate = async ({ text, source, target, fieldKey, applyFn }) => {
    if (!text || !text.trim()) return;
    setTranslatingField(fieldKey);
    try {
      const res = await axios.post(
        `${API_URL}/api/utils/translate`,
        { text: text.trim(), source, target },
        { headers: getAuthHeaders() },
      );
      const translated = (res.data?.translated || '').trim();
      if (translated) applyFn(translated);
    } catch (err) {
      toast.error(isAr ? 'فشل في الترجمة التلقائية' : 'Auto-translate failed');
    } finally {
      setTranslatingField(null);
    }
  };

  const fetchCodes = useCallback(async () => {
    if (!emirate || !category) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/codes`,
        { headers: getAuthHeaders(), params: { emirate, category } },
      );
      setCodes(res.data?.data || []);
    } catch (e) {
      toast.error(isAr ? 'فشل تحميل الأكواد' : 'Failed to load codes');
    } finally {
      setLoading(false);
    }
  }, [emirate, category, isAr]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  // -------- handlers --------
  const openCreate = () => {
    setForm({ ...EMPTY_FORM, sort_order: codes.length + 1 });
    setEditing('new');
  };

  const openEdit = (c) => {
    setForm({
      code: c.code || '',
      code_ar: c.code_ar || '',
      show_on_plate: c.show_on_plate !== false,
      label_ar: c.label_ar || '',
      label_en: c.label_en || '',
      image_url: c.image_url || '',
      is_active: c.is_active !== false,
      sort_order: c.sort_order ?? 0,
    });
    setEditing(c);
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(isAr ? 'الملف يجب أن يكون صورة' : 'File must be an image');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(
        isAr
          ? `الصورة كبيرة جداً (الحد ${Math.round(MAX_IMAGE_BYTES / 1024)}KB)`
          : `Image too large (max ${Math.round(MAX_IMAGE_BYTES / 1024)}KB)`,
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_url: String(reader.result || '') }));
    reader.onerror = () => toast.error(isAr ? 'فشل قراءة الصورة' : 'Read failed');
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    const codeEn = form.code.trim();
    const codeAr = form.code_ar.trim();
    if (!codeEn && !codeAr) {
      toast.error(isAr ? 'اكتب الكود بالعربي أو بالإنجليزي على الأقل' : 'Enter the code in Arabic or English at least');
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(
          `${BASE}/codes`,
          {
            emirate,
            category,
            code: codeEn || codeAr,
            code_ar: codeAr || codeEn,
            show_on_plate: form.show_on_plate,
            label_ar: form.label_ar.trim(),
            label_en: form.label_en.trim(),
            image_url: form.image_url,
            is_active: form.is_active,
            sort_order: Number(form.sort_order) || 0,
          },
          { headers: getAuthHeaders() },
        );
        toast.success(isAr ? 'تم إضافة الكود' : 'Code added');
      } else if (editing && editing.id) {
        await axios.put(
          `${BASE}/codes/${editing.id}`,
          {
            code: codeEn || codeAr,
            code_ar: codeAr || codeEn,
            show_on_plate: form.show_on_plate,
            label_ar: form.label_ar.trim(),
            label_en: form.label_en.trim(),
            image_url: form.image_url,
            is_active: form.is_active,
            sort_order: Number(form.sort_order) || 0,
          },
          { headers: getAuthHeaders() },
        );
        toast.success(isAr ? 'تم الحفظ' : 'Saved');
      }
      setEditing(null);
      setForm(EMPTY_FORM);
      fetchCodes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const submitDelete = async () => {
    if (!confirmDelete?.id) return;
    setDeletingId(confirmDelete.id);
    try {
      await axios.delete(`${BASE}/codes/${confirmDelete.id}`, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      setConfirmDelete(null);
      fetchCodes();
    } catch (e) {
      toast.error(isAr ? 'فشل الحذف' : 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.put(
        `${BASE}/codes/${c.id}`,
        { is_active: !c.is_active },
        { headers: getAuthHeaders() },
      );
      setCodes((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_active: !c.is_active } : x)));
    } catch (e) {
      toast.error(isAr ? 'فشل التغيير' : 'Toggle failed');
    }
  };

  if (!emirateMeta || !categoryMeta) return null;
  const CatIcon = categoryMeta.icon;

  return (
    <DashboardLayout
      title={`${isAr ? emirateMeta.ar : emirateMeta.en} — ${isAr ? categoryMeta.ar : categoryMeta.en}`}
      titleEn={`${emirateMeta.en} — ${categoryMeta.en}`}
      subtitle="إدارة أكواد لوحات السيارات لهذه الفئة"
      subtitleEn="Manage plate codes for this category"
      screenCode="BIZ-C04-M"
      backPath="/control/cloudgm-business/plate-codes"
      onBack={() => navigate('/control/cloudgm-business/plate-codes')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/control/cloudgm-business' },
        { labelAr: 'أكواد اللوحات', labelEn: 'Plate Codes', path: '/control/cloudgm-business/plate-codes' },
        { labelAr: `${emirateMeta.ar} — ${categoryMeta.ar}`, labelEn: `${emirateMeta.en} — ${categoryMeta.en}` },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="plate-codes-manager">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl border flex items-center justify-center"
              style={{ backgroundColor: `${emirateMeta.color}15`, borderColor: `${emirateMeta.color}40` }}
            >
              <CatIcon className="h-6 w-6" style={{ color: emirateMeta.color }} />
            </div>
            <div>
              <p className="text-xs text-[#71717A]">{isAr ? 'الفئة' : 'Category'}</p>
              <p className="text-white text-base font-bold flex items-center gap-2">
                <span style={{ color: categoryMeta.color }}>{isAr ? categoryMeta.ar : categoryMeta.en}</span>
                <span className="text-[#71717A]">·</span>
                <span className="text-[#A1A1AA]">{isAr ? emirateMeta.ar : emirateMeta.en}</span>
              </p>
              <p className="text-[10px] text-[#71717A]">
                {codes.length} {isAr ? 'كود مُعرَّف' : 'codes defined'}
              </p>
            </div>
          </div>
          <Button
            onClick={openCreate}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="add-code-btn"
          >
            <Plus className="h-4 w-4" />
            {isAr ? 'إضافة كود' : 'Add Code'}
          </Button>
        </div>

        {/* Codes grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-emerald-500" />
          </div>
        ) : codes.length === 0 ? (
          <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-12 text-center" data-testid="codes-empty">
            <div className="w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-emerald-500/10 border border-emerald-500/30">
              <Hash className="h-10 w-10 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-1">
              {isAr ? 'لا توجد أكواد بعد' : 'No codes yet'}
            </h3>
            <p className="text-sm text-[#A1A1AA] mb-5">
              {isAr
                ? `ابدأ بإضافة أول كود لوحة ${categoryMeta.ar} في ${emirateMeta.ar}`
                : `Add the first ${categoryMeta.en} plate code in ${emirateMeta.en}`}
            </p>
            <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
              <Plus className="h-4 w-4" />
              {isAr ? 'إضافة كود' : 'Add Code'}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="codes-grid">
            {codes.map((c, idx) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx * 0.02, 0.3) }}
                className={`bg-white/[0.02] backdrop-blur-xl border rounded-xl overflow-hidden hover:border-white/20 transition-colors ${
                  c.is_active ? 'border-white/10' : 'border-white/5 opacity-60'
                }`}
                data-testid={`code-card-${c.id}`}
              >
                {/* Image / live plate design */}
                <div className="relative aspect-[16/9] bg-[#0a0a0a] flex items-center justify-center overflow-hidden">
                  {designAvailable ? (
                    <div className="p-2 flex items-center justify-center w-full h-full">
                      <UAEPlate
                        emirate={emirate}
                        category={category}
                        code={c.show_on_plate === false ? '' : c.code}
                        number=""
                        width={category === 'motorcycle' ? 135 : 185}
                      />
                    </div>
                  ) : c.image_url ? (
                    <img src={c.image_url} alt={c.code} className="w-full h-full object-contain" />
                  ) : (
                    <ImageOff className="h-8 w-8 text-[#3a3a3a]" />
                  )}
                  {!c.is_active && (
                    <span className="absolute top-1 start-1 text-[9px] px-1.5 py-0.5 rounded-md bg-red-500/15 border border-red-500/40 text-red-300 font-bold">
                      {isAr ? 'معطّل' : 'OFF'}
                    </span>
                  )}
                </div>

                {/* Body */}
                <div className="p-2">
                  <p className="text-white font-bold text-base text-center mb-1">
                    {isAr ? (c.code_ar || c.code) : (c.code || c.code_ar)}
                  </p>
                  {(c.label_ar || c.label_en) && (
                    <p className="text-[10px] text-[#A1A1AA] text-center truncate" title={isAr ? c.label_ar : c.label_en}>
                      {isAr ? (c.label_ar || c.label_en) : (c.label_en || c.label_ar)}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                    <Switch
                      checked={c.is_active}
                      onCheckedChange={() => toggleActive(c)}
                      data-testid={`code-toggle-${c.id}`}
                    />
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit(c)}
                        className="p-1 rounded-md text-[#A1A1AA] hover:text-white hover:bg-white/5"
                        title={isAr ? 'تعديل' : 'Edit'}
                        data-testid={`code-edit-${c.id}`}
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(c)}
                        className="p-1 rounded-md text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        title={isAr ? 'حذف' : 'Delete'}
                        data-testid={`code-delete-${c.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Add/Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setForm(EMPTY_FORM); } }}>
        <DialogContent
          className="bg-[#0a0a0a] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto"
          data-testid="code-form-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editing === 'new'
                ? <Plus className="h-5 w-5 text-emerald-400" />
                : <Edit3 className="h-5 w-5 text-emerald-400" />}
              {editing === 'new'
                ? (isAr ? 'إضافة كود جديد' : 'Add new code')
                : (isAr ? 'تعديل الكود' : 'Edit code')}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA] text-xs">
              {isAr ? emirateMeta.ar : emirateMeta.en} · {isAr ? categoryMeta.ar : categoryMeta.en}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {designAvailable ? (
              /* معاينة حية — اللوحة الرسمية تتحدث مع الكتابة */
              <div>
                <label className="text-xs text-[#A1A1AA] mb-2 block">
                  {isAr ? 'معاينة اللوحة (تتحدث مباشرة أثناء الكتابة)' : 'Live plate preview'}
                </label>
                <div
                  className="bg-[#18181b] rounded-xl border border-white/10 p-4 flex items-center justify-center"
                  data-testid="live-plate-preview"
                >
                  <UAEPlate
                    emirate={emirate}
                    category={category}
                    code={form.show_on_plate ? (form.code || form.code_ar) : ''}
                    number=""
                    width={category === 'motorcycle' ? 240 : 340}
                  />
                </div>
                <p className="mt-2 text-[10px] text-[#71717A]">
                  {isAr
                    ? 'اللوحة تعرض الكود فقط — الأرقام يكتبها العملاء والتجار في منصاتهم'
                    : 'The plate shows the code only — numbers are entered by customers/merchants in their platforms'}
                </p>
              </div>
            ) : (
            <div>
              <label className="text-xs text-[#A1A1AA] mb-2 block">
                {isAr ? 'صورة اللوحة' : 'Plate image'}
              </label>
              <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
                {form.image_url ? (
                  <div className="relative">
                    <img src={form.image_url} alt="preview" className="w-full aspect-[16/9] object-contain" />
                    <button
                      onClick={() => setForm({ ...form, image_url: '' })}
                      className="absolute top-2 end-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-md text-white"
                      data-testid="clear-image"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="aspect-[16/9] flex flex-col items-center justify-center text-[#71717A]">
                    <ImageIcon className="h-10 w-10 mb-2" />
                    <p className="text-xs">{isAr ? 'لا توجد صورة' : 'No image'}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-2">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={onPickFile}
                  className="hidden"
                  data-testid="image-file-input"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  size="sm"
                  className="border-white/10 text-white hover:bg-white/5 gap-1 flex-1"
                  data-testid="upload-image-btn"
                >
                  <Upload className="h-3.5 w-3.5" />
                  {isAr ? 'رفع صورة' : 'Upload'}
                </Button>
              </div>
              <p className="text-[10px] text-[#71717A] mt-1">
                {isAr
                  ? `بحد أقصى ${Math.round(MAX_IMAGE_BYTES / 1024)}KB · PNG / JPG / WEBP / SVG.`
                  : `Max ${Math.round(MAX_IMAGE_BYTES / 1024)}KB · PNG / JPG / WEBP / SVG.`}
              </p>
            </div>
            )}

            {/* Code — bilingual free-form fields + translate buttons */}
            <div className="space-y-3">
              {/* Show-on-plate checkbox — deliberate choice per code */}
              <label
                className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 cursor-pointer transition ${form.show_on_plate
                  ? 'bg-emerald-500/[0.06] border-emerald-500/40'
                  : 'bg-white/[0.03] border-white/10'}`}
                data-testid="show-on-plate-row"
              >
                <input
                  type="checkbox"
                  checked={form.show_on_plate}
                  onChange={(e) => setForm({ ...form, show_on_plate: e.target.checked })}
                  className="mt-0.5 h-4 w-4 accent-emerald-500 cursor-pointer"
                  data-testid="show-on-plate-checkbox"
                />
                <span>
                  <span className={`block text-sm font-bold ${form.show_on_plate ? 'text-emerald-300' : 'text-white/70'}`}>
                    {isAr ? 'عرض الكود على اللوحة' : 'Show code on the plate'}
                  </span>
                  <span className="block text-[10px] text-[#71717A] mt-0.5 leading-relaxed">
                    {isAr
                      ? 'إذا لم تفعّله: يظهر الكود في القائمة المنسدلة فقط (منصة العملاء والتجار) وتظهر اللوحة بدون أي كتابة — مثل "كلاسيكي" أو "أبيض".'
                      : 'If unchecked: the code appears only in the dropdown (customer/merchant platforms) and the plate is rendered without any text — e.g., "Classic" or "White".'}
                  </span>
                </span>
              </label>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-[#A1A1AA] block">
                    {isAr ? 'الكود بالعربي *' : 'Code (Arabic) *'}
                  </label>
                  <button
                    type="button"
                    onClick={() => autoTranslate({
                      text: form.code_ar, source: 'ar', target: 'en', fieldKey: 'code_ar',
                      applyFn: (t) => setForm((f) => ({ ...f, code: t })),
                    })}
                    disabled={!form.code_ar.trim() || !!translatingField}
                    className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37]/20 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    data-testid="translate-code-ar-to-en"
                  >
                    {translatingField === 'code_ar'
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Languages className="h-3 w-3" />}
                    {isAr ? 'ترجمة إلى الإنجليزية' : 'Translate to English'}
                  </button>
                </div>
                <Input
                  value={form.code_ar}
                  onChange={(e) => setForm({ ...form, code_ar: e.target.value })}
                  placeholder={isAr ? 'مثال: A أو كلاسيكي' : 'e.g., A or كلاسيكي'}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="code-ar-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-[#A1A1AA] block">
                    {isAr ? 'الكود بالإنجليزي *' : 'Code (English) *'}
                  </label>
                  <button
                    type="button"
                    onClick={() => autoTranslate({
                      text: form.code, source: 'en', target: 'ar', fieldKey: 'code_en',
                      applyFn: (t) => setForm((f) => ({ ...f, code_ar: t })),
                    })}
                    disabled={!form.code.trim() || !!translatingField}
                    className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37]/20 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    data-testid="translate-code-en-to-ar"
                  >
                    {translatingField === 'code_en'
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Languages className="h-3 w-3" />}
                    {isAr ? 'ترجمة إلى العربية' : 'Translate to Arabic'}
                  </button>
                </div>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder={isAr ? 'مثال: A أو AA أو Classic' : 'e.g., A or AA or Classic'}
                  className="bg-white/5 border-white/10 text-white font-mono"
                  dir="ltr"
                  data-testid="code-input"
                />
              </div>
              <p className="text-[10px] text-[#71717A]">
                {isAr
                  ? 'الحقلان حرّان — يقبلان أي أحرف (عربي أو إنجليزي). مثال: كود AA اكتبه إنجليزي في الحقلين، وكود كلاسيكي اكتبه عربي وإنجليزي.'
                  : 'Both fields are free-form — any characters accepted. e.g., code AA in English in both, or كلاسيكي/Classic.'}
              </p>
              {/* Read-only category derived from the currently-selected category */}
              <div className="flex items-center gap-2 text-[11px]" data-testid="code-category-display">
                <span className="text-[#71717A]">
                  {isAr ? 'الفئة:' : 'Category:'}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 font-semibold">
                  {isAr ? categoryMeta.ar : categoryMeta.en}
                </span>
              </div>
            </div>

            {/* Sort + Active */}
            <div className="grid grid-cols-2 gap-2 items-center">
              <div>
                <label className="text-xs text-[#A1A1AA] mb-1 block">
                  {isAr ? 'ترتيب العرض' : 'Sort order'}
                </label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  className="bg-white/5 border-white/10 text-white text-sm"
                  dir="ltr"
                  data-testid="sort-order-input"
                />
              </div>
              <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-md px-3 h-9 mt-5">
                <span className="text-sm text-white">
                  {isAr ? 'مفعّل' : 'Active'}
                </span>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                  data-testid="form-active-toggle"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setEditing(null); setForm(EMPTY_FORM); }}
              className="border-white/10 text-white hover:bg-white/5"
            >
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              data-testid="submit-code-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isAr ? 'حفظ' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              {isAr ? 'حذف الكود' : 'Delete code'}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {isAr
                ? 'سيتم حذف الكود نهائياً. هذا الإجراء لا يمكن التراجع عنه.'
                : 'This code will be permanently deleted. This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          {confirmDelete && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-sm">
              <span className="text-[#71717A] me-2">{isAr ? 'الكود:' : 'Code:'}</span>
              <span className="text-white font-mono font-bold">{confirmDelete.code}</span>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={submitDelete}
              disabled={!!deletingId}
              className="bg-red-500 hover:bg-red-600 text-white gap-2"
              data-testid="confirm-delete-btn"
            >
              {deletingId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {isAr ? 'حذف' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
